import { ThirdPartyConfigsSync } from '../../../common/ui/configs/sync';
import { ProductKeys3P } from '../../../common/ui/configs/types';

/**
 * @internal Only for use by `coerceFriendlyName`.
 *
 * Hard-coded English fallbacks for connectors whose static
 * {@link ThirdPartyConfigsSync} `displayName` is a non-string `React.ReactNode`
 * (e.g. a `<FormattedMessage />`), which we cannot resolve to a string
 * synchronously here.
 *
 * KNOWN EDGE CASE (RAOC-23 — to review): `atlassian-web-crawler` declares its
 * display name as a translatable `<FormattedMessage />`, so it cannot be
 * resolved to a string synchronously and falls back to this fixed English
 * string ("Web Pages"). This is the connector's canonical chat-side label: it
 * is intentionally NOT given `workspaceName` precedence (unlike the DC
 * connectors) because the chat path collapses all crawler instances into one
 * connector. We deliberately do NOT solve full i18n here — that is a larger
 * piece of the iterative migration.
 *
 * Downstreams of conversation-assistant-store do not implement i18n today, so there is no new regression from this code
 *
 * TODO RAOC-23: Improve downstream code sites to accept `string | React.ReactNode` for display names, allowing i18n names, and removing this fallback
 */
const REACT_NODE_DISPLAY_NAME_FALLBACKS: Partial<Record<ProductKeys3P, string>> = {
	[ProductKeys3P.AtlassianWebCrawler]: 'Web Pages',
};

/**
 * @internal Only for use by `coerceFriendlyName`.
 *
 * Connectors for which the per-instance `workspaceName` from the combined-config
 * response should take precedence over the generic static catalogue name.
 *
 * `workspaceName` is opt-in (rather than applied to every connector) because its
 * meaning varies by connector type:
 *
 * TODO RAOC-23: Move this to be a variable in the config objects, and also support this in search downstream, which has its own specical logic handling this.
 */
const WORKSPACE_NAME_PRECEDENCE_KEYS: ReadonlySet<string> = new Set<string>([
	// NOTE: `atlassian-web-crawler` is deliberately NOT in this allowlist.
	// In Search the crawler is fanned out into one row per indexed site, each
	// labelled with its `workspaceName` (see `displayWorkspaceNameAsProductName`
	// / `fanOutConnectorSources` on the web-crawler config). On the chat path,
	// however, every crawler instance collapses into a single
	// `atlassian-web-crawler` connector, so taking one instance's
	// `workspaceName` would surface an arbitrary site name (e.g. "Atlassian
	// Community") and sort it under that name rather than the connector's own
	// identity. The crawler must instead resolve to its catalogue/fallback name
	// "Web Pages" (see `REACT_NODE_DISPLAY_NAME_FALLBACKS` below) so it sorts and
	// displays consistently. See RAOC-23.
	//
	// FC Connectors: used to distinguish from cloud products (Cloud: Confluence, DC: Bullhorn, etc.)
	ProductKeys3P.JiraDC,
	ProductKeys3P.JsmDC,
	ProductKeys3P.ConfluenceDC,
]);

/**
 * @internal Only for use by `useRovoConfigStore`.
 *
 * Resolves a human-readable connector name.
 */
export const coerceFriendlyName = (
	displayName: unknown,
	workspaceName?: unknown,
	productKey?: string,
): string => {
	if (
		productKey !== undefined &&
		WORKSPACE_NAME_PRECEDENCE_KEYS.has(productKey) &&
		typeof workspaceName === 'string' &&
		workspaceName !== ''
	) {
		return workspaceName;
	}

	if (typeof displayName === 'string' && displayName !== '') {
		return displayName;
	}

	if (productKey) {
		const staticDisplayName = ThirdPartyConfigsSync.get(productKey as ProductKeys3P)?.displayName;
		if (typeof staticDisplayName === 'string' && staticDisplayName !== '') {
			return staticDisplayName;
		}

		const fallback = REACT_NODE_DISPLAY_NAME_FALLBACKS[productKey as ProductKeys3P];
		if (fallback) {
			return fallback;
		}
	}

	return '';
};
